import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import styled from 'styled-components';
import { getTree } from '../../../utils/tree.js';
import { useTreeA11y } from '../hooks/use_tree_a11y.js';
import { TreeA11yContext } from './TreeA11yContext.js';
import { TreeNodeBase } from './TreeNodeBase.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["a11yDragId", "canDrag", "canDrop", "filterBy", "focusIndex", "getAriaDropEffect", "getAriaGrabbed", "getAriaSelected", "hiddenIds", "hideBy", "nodeById", "onChange", "onChangeHiddenIds", "onCollapse", "onDragStart", "onExpand", "onFocusIndexChange", "orderBy", "renderNode", "setA11yDragId", "setHiddenIds", "variant"];
var StyledTreeBase = styled.div.withConfig({
  displayName: "TreeBase__StyledTreeBase",
  componentId: "sc-k1wie7-0"
})({
  "&:focus": {
    outline: "none"
  }
});
var TreeBase = function TreeBase(_ref) {
  var a11yDragId = _ref.a11yDragId,
    canDrag = _ref.canDrag,
    canDrop = _ref.canDrop,
    filterBy = _ref.filterBy,
    focusIndexProp = _ref.focusIndex,
    _ref$getAriaDropEffec = _ref.getAriaDropEffect,
    getAriaDropEffect = _ref$getAriaDropEffec === void 0 ? function () {
      return void 0;
    } : _ref$getAriaDropEffec,
    _ref$getAriaGrabbed = _ref.getAriaGrabbed,
    getAriaGrabbed = _ref$getAriaGrabbed === void 0 ? function () {
      return void 0;
    } : _ref$getAriaGrabbed,
    _ref$getAriaSelected = _ref.getAriaSelected,
    getAriaSelected = _ref$getAriaSelected === void 0 ? function () {
      return void 0;
    } : _ref$getAriaSelected,
    _ref$hiddenIds = _ref.hiddenIds,
    hiddenIds = _ref$hiddenIds === void 0 ? new Set() : _ref$hiddenIds,
    hideBy = _ref.hideBy,
    nodeById = _ref.nodeById,
    onChange = _ref.onChange,
    onChangeHiddenIds = _ref.onChangeHiddenIds,
    onCollapse = _ref.onCollapse,
    onDragStart = _ref.onDragStart,
    onExpand = _ref.onExpand,
    onFocusIndexChangeProp = _ref.onFocusIndexChange,
    orderBy = _ref.orderBy,
    renderNode = _ref.renderNode,
    setA11yDragId = _ref.setA11yDragId,
    setHiddenIds = _ref.setHiddenIds,
    variant = _ref.variant,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _React$useMemo = React.useMemo(function () {
      return getTree({
        filterBy: filterBy,
        hideBy: hideBy,
        nodeById: nodeById,
        orderBy: orderBy
      });
    }, [filterBy, hideBy, nodeById, orderBy]),
    context = _React$useMemo.context,
    nodeIds = _React$useMemo.nodeIds;
  var _React$useState = React.useState(focusIndexProp),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    focusIndex = _React$useState2[0],
    setFocusIndex = _React$useState2[1];
  React.useEffect(function () {
    setFocusIndex(focusIndexProp);
  }, [focusIndexProp]);
  var onFocusIndexChange = function onFocusIndexChange(val) {
    if (onFocusIndexChangeProp) {
      onFocusIndexChangeProp(val);
    }
    setFocusIndex(val);
  };
  var _useTreeA11y = useTreeA11y({
      a11yDragId: a11yDragId,
      canDrag: canDrag,
      canDrop: canDrop,
      context: context,
      hiddenIds: hiddenIds,
      nodeById: nodeById,
      onChange: onChange,
      onChangeHiddenIds: onChangeHiddenIds,
      onCollapse: onCollapse,
      onDragStart: onDragStart,
      onExpand: onExpand,
      onFocusIndexChange: onFocusIndexChange,
      setA11yDragId: setA11yDragId,
      setHiddenIds: setHiddenIds,
      variant: variant
    }),
    focusedDropRegion = _useTreeA11y.focusedDropRegion,
    focusedNodeId = _useTreeA11y.focusedNodeId,
    handleKeyDown = _useTreeA11y.handleKeyDown;
  return /*#__PURE__*/jsx(TreeA11yContext, {
    draggedNodeId: a11yDragId,
    focusedNodeId: focusedNodeId,
    focusedDropRegion: focusedDropRegion,
    children: /*#__PURE__*/jsx(StyledTreeBase, {
      role: "treegrid",
      "aria-multiselectable": rest["aria-multiselectable"],
      "aria-rowcount": context.getNodeCount(),
      "data-testid": "mntl-tree-base",
      onFocus: function onFocus() {
        return onFocusIndexChange(0);
      },
      tabIndex: 0,
      children: nodeIds.map(function (id) {
        var node = nodeById[id];
        var nodeContext = context.getNodeContext(id);
        var focused = focusIndex === context.getNodeContext(id).absoluteIndex;
        var renderProps = {
          context: nodeContext,
          focused: focused,
          node: node
        };
        return /*#__PURE__*/jsx(TreeNodeBase, {
          "aria-dropeffect": getAriaDropEffect(renderProps),
          "aria-expanded": nodeContext.getAriaExpanded(),
          "aria-grabbed": getAriaGrabbed(renderProps),
          "aria-level": nodeContext.getAriaLevel(),
          "aria-posinset": nodeContext.getAriaPosInSet(),
          "aria-selected": getAriaSelected(renderProps),
          "aria-setsize": nodeContext.getAriaSetSize(),
          "aria-rowindex": nodeContext.getAriaRowIndex(),
          "data-testid": "mntl-tree-node-base",
          focused: focused,
          onBlur: function onBlur(event) {
            if (event.target.getAttribute("role") === "row") {
              onFocusIndexChange(void 0);
            }
          },
          onFocus: function onFocus(event) {
            event.stopPropagation();
            onFocusIndexChange(nodeContext.absoluteIndex);
          },
          onKeyDown: function onKeyDown(event) {
            return handleKeyDown(event, id);
          },
          role: "row",
          tabIndex: focused ? 0 : -1,
          children: renderNode(renderProps)
        }, nodeContext.id);
      })
    })
  });
};
TreeBase.displayName = "TreeBase";

export { StyledTreeBase, TreeBase };
